import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { alert } from '../../hooks/function';
import { quizClient } from '../../apis/client-instances';
import { QuestionType } from '../../apis/api-client';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  SelectField,
  TextField,
  Typography,
} from '../ui';
import { AddIcon, CheckIcon, DeleteIcon, DragIndicatorIcon } from '../icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TiptapEditor from '../common/TiptapEditor';

interface QuizEditorProps {
  lessonId: number;
}

interface EditorOption {
  text: string;
  isCorrect: boolean;
}

interface EditorQuestion {
  questionText: string;
  questionType: QuestionType;
  options: EditorOption[];
}

const QUESTION_TYPES = [
  { label: 'True / False', value: QuestionType.TrueFalse },
  { label: 'Single Choice', value: QuestionType.SingleChoice },
  { label: 'Multiple Choice', value: QuestionType.MultipleChoice },
];

const DEFAULT_TF_OPTIONS: EditorOption[] = [
  { text: 'True', isCorrect: true },
  { text: 'False', isCorrect: false },
];

const DEFAULT_CHOICE_OPTIONS: EditorOption[] = [
  { text: '', isCorrect: true },
  { text: '', isCorrect: false },
];

// Sortable question card component
function SortableQuestionCard({
  question,
  qIndex,
  onRemove,
  onUpdateText,
  onUpdateType,
  onUpdateOptionText,
  onSetCorrect,
  onToggleCorrect,
  onAddOption,
  onRemoveOption,
}: {
  question: EditorQuestion;
  qIndex: number;
  onRemove: () => void;
  onUpdateText: (text: string) => void;
  onUpdateType: (type: QuestionType) => void;
  onUpdateOptionText: (oIndex: number, text: string) => void;
  onSetCorrect: (oIndex: number) => void;
  onToggleCorrect: (oIndex: number) => void;
  onAddOption: () => void;
  onRemoveOption: (oIndex: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `question-${qIndex}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isTrueFalse = question.questionType === QuestionType.TrueFalse;
  const isMultipleChoice = question.questionType === QuestionType.MultipleChoice;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
      }}
    >
      {/* Question header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {/* Drag Handle */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            padding: '4px',
            ':active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ width: '20px', height: '20px', color: 'grey.500', opacity: 0.7 }} />
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 0 }}>
          Q{qIndex + 1}
        </Typography>
        <SelectField
          label=""
          value={question.questionType}
          onChange={(e) => onUpdateType(Number(e.target.value) as QuestionType)}
          options={QUESTION_TYPES.map((t) => ({ label: t.label, value: t.value }))}
          allowEmpty={false}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onRemove} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Question text */}
      <TiptapEditor
        content={question.questionText}
        onChange={onUpdateText}
        placeholder="Enter question text..."
        sx={{ mb: 2 }}
      />

      {/* Correct answer banner */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1.5,
          px: 1.5,
          py: 0.75,
          bgcolor: 'success.50',
          border: '1px solid',
          borderColor: 'success.200',
          borderRadius: 1,
        }}
      >
        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
        <Typography variant="caption" color="success.dark" fontWeight={500}>
          {isMultipleChoice
            ? 'Select all correct answers below'
            : 'Select the correct answer below'}
        </Typography>
      </Box>

      {/* Options */}
      {isTrueFalse ? (
        <RadioGroup
          value={question.options.findIndex((o) => o.isCorrect)}
          onChange={(e) => onSetCorrect(parseInt(e.target.value))}
        >
          {question.options.map((option, oIndex) => (
            <FormControlLabel
              key={oIndex}
              value={oIndex}
              control={<Radio size="small" />}
              label={option.text}
            />
          ))}
        </RadioGroup>
      ) : !isMultipleChoice ? (
        <RadioGroup
          value={question.options.findIndex((o) => o.isCorrect)}
          onChange={(e) => onSetCorrect(parseInt(e.target.value))}
        >
          {question.options.map((option, oIndex) => (
            <Box key={oIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Radio value={oIndex} size="small" />
              <TextField
                placeholder={`Option ${oIndex + 1}`}
                value={option.text}
                onChange={(e) => onUpdateOptionText(oIndex, e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              {question.options.length > 2 && (
                <IconButton size="small" onClick={() => onRemoveOption(oIndex)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </RadioGroup>
      ) : (
        <Box>
          {question.options.map((option, oIndex) => (
            <Box key={oIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Checkbox
                checked={option.isCorrect}
                onChange={() => onToggleCorrect(oIndex)}
                size="small"
              />
              <TextField
                placeholder={`Option ${oIndex + 1}`}
                value={option.text}
                onChange={(e) => onUpdateOptionText(oIndex, e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              {question.options.length > 2 && (
                <IconButton size="small" onClick={() => onRemoveOption(oIndex)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Add option button (not for True/False) */}
      {!isTrueFalse && (
        <Button
          size="small"
          variant="text"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddOption}
          sx={{ mt: 1 }}
        >
          Add Option
        </Button>
      )}
    </Box>
  );
}

export default function QuizEditor({ lessonId }: QuizEditorProps) {
  const [questions, setQuestions] = useState<EditorQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const data = await quizClient.getQuestionsForEditor(lessonId);
        if (data.length > 0) {
          setQuestions(
            data.map((q: any) => ({
              questionText: q.questionText,
              questionType: q.questionType,
              options: q.options.map((o: any) => ({ text: o.text, isCorrect: o.isCorrect })),
            }))
          );
        }
      } catch {
        // New quiz - no existing questions
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [lessonId]);

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        questionText: '',
        questionType: QuestionType.SingleChoice,
        options: [...DEFAULT_CHOICE_OPTIONS.map((o) => ({ ...o }))],
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuestionText = (index: number, text: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, questionText: text } : q)));
  };

  const updateQuestionType = (index: number, type: QuestionType) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;

        let options: EditorOption[];
        if (type === QuestionType.TrueFalse) {
          options = DEFAULT_TF_OPTIONS.map((o) => ({ ...o }));
        } else {
          options =
            q.questionType === QuestionType.TrueFalse
              ? DEFAULT_CHOICE_OPTIONS.map((o) => ({ ...o }))
              : q.options;
        }

        return { ...q, questionType: type, options };
      })
    );
  };

  const updateOptionText = (qIndex: number, oIndex: number, text: string) => {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex
          ? { ...q, options: q.options.map((o, oi) => (oi === oIndex ? { ...o, text } : o)) }
          : q
      )
    );
  };

  const setCorrectOption = (qIndex: number, oIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex
          ? { ...q, options: q.options.map((o, oi) => ({ ...o, isCorrect: oi === oIndex })) }
          : q
      )
    );
  };

  const toggleCorrectOption = (qIndex: number, oIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex
          ? {
              ...q,
              options: q.options.map((o, oi) => (oi === oIndex ? { ...o, isCorrect: !o.isCorrect } : o)),
            }
          : q
      )
    );
  };

  const addOption = (qIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex ? { ...q, options: [...q.options, { text: '', isCorrect: false }] } : q
      )
    );
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    setQuestions((prev) =>
      prev.map((q, qi) =>
        qi === qIndex ? { ...q, options: q.options.filter((_, oi) => oi !== oIndex) } : q
      )
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIndex = parseInt(String(active.id).replace('question-', ''));
    const overIndex = parseInt(String(over.id).replace('question-', ''));

    setQuestions((prev) => arrayMove(prev, activeIndex, overIndex));
  };

  const handleSave = async () => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      // An empty Tiptap editor still emits markup ("<p></p>"), so check the text.
      if (!q.questionText.replace(/<[^>]*>/g, '').trim()) {
        alert(enqueueSnackbar, `Question ${i + 1} is missing text`, 'error');
        return;
      }
      if (q.options.length < 2) {
        alert(enqueueSnackbar, `Question ${i + 1} needs at least 2 options`, 'error');
        return;
      }
      const hasCorrect = q.options.some((o) => o.isCorrect);
      if (!hasCorrect) {
        alert(enqueueSnackbar, `Question ${i + 1} needs at least 1 correct answer`, 'error');
        return;
      }
      const emptyOption = q.options.find((o) => !o.text.trim());
      if (emptyOption) {
        alert(enqueueSnackbar, `Question ${i + 1} has an empty option`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      await quizClient.saveQuestions(lessonId, { questions });
      alert(enqueueSnackbar, 'Quiz questions saved', 'success');
    } catch (error) {
      console.error('Failed to save questions:', error);
      alert(enqueueSnackbar, 'Failed to save questions', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  const sortableIds = questions.map((_, i) => `question-${i}`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {questions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No questions yet. Click "Add Question" to get started.
        </Typography>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {questions.map((question, qIndex) => (
            <SortableQuestionCard
              key={qIndex}
              question={question}
              qIndex={qIndex}
              onRemove={() => removeQuestion(qIndex)}
              onUpdateText={(text) => updateQuestionText(qIndex, text)}
              onUpdateType={(type) => updateQuestionType(qIndex, type)}
              onUpdateOptionText={(oIndex, text) => updateOptionText(qIndex, oIndex, text)}
              onSetCorrect={(oIndex) => setCorrectOption(qIndex, oIndex)}
              onToggleCorrect={(oIndex) => toggleCorrectOption(qIndex, oIndex)}
              onAddOption={() => addOption(qIndex)}
              onRemoveOption={(oIndex) => removeOption(qIndex, oIndex)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={addQuestion}>
          Add Question
        </Button>
        {questions.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save Questions'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
